import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { 
    Plus, Edit, Trash2, Route, PlayCircle, CheckCircle, 
    FileText, Clock, AlertTriangle, AlertOctagon, Star,
    ChevronRight, Loader2, Copy, Settings2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iconos para las etapas
const iconMap = {
    'play': PlayCircle,
    'check': CheckCircle,
    'file-text': FileText,
    'clock': Clock,
    'alert-triangle': AlertTriangle,
    'alert-octagon': AlertOctagon,
    'check-circle': CheckCircle,
};

const RutasCobro = () => {
    const { canEdit } = useAuth();
    const [loading, setLoading] = useState(true);
    const [rutas, setRutas] = useState([]);
    const [selectedRuta, setSelectedRuta] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [etapaDialogOpen, setEtapaDialogOpen] = useState(false);
    const [editingEtapa, setEditingEtapa] = useState(null);
    const [seeding, setSeeding] = useState(false);
    
    const [rutaForm, setRutaForm] = useState({
        nombre: '',
        descripcion: '',
        codigo: '',
        es_default: false,
        requiere_edp: false,
        requiere_orden_compra: false,
        dia_envio_edp: '',
        dia_limite_oc: '',
        dia_facturacion: '',
        dias_pago_default: 30,
        dias_gracia_mora: 5
    });

    const [etapaForm, setEtapaForm] = useState({
        nombre: '',
        codigo: '',
        orden: 0,
        tipo_trigger: 'manual',
        dias_trigger: '',
        dia_mes_trigger: '',
        documento_requerido: 'ninguno',
        bloquea_avance_sin_documento: false,
        accion_entrada: 'ninguna',
        genera_alerta: false,
        mensaje_alerta: '',
        color: '#3B82F6',
        es_etapa_final: false,
        es_etapa_error: false
    });

    useEffect(() => {
        fetchRutas();
    }, []);

    const fetchRutas = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/rutas-cobro`);
            setRutas(response.data);
        } catch (error) {
            console.error('Error fetching rutas:', error);
            toast.error('Error al cargar rutas');
        } finally {
            setLoading(false);
        }
    };

    const fetchRutaDetail = async (rutaId) => {
        try {
            const response = await axios.get(`${API_URL}/api/rutas-cobro/${rutaId}`);
            setSelectedRuta(response.data);
        } catch (error) {
            console.error('Error fetching ruta detail:', error);
            toast.error('Error al cargar detalle de ruta');
        }
    };

    const handleSeedTemplates = async () => {
        setSeeding(true);
        try {
            await axios.post(`${API_URL}/api/rutas-cobro/seed-templates`);
            toast.success('Plantillas creadas exitosamente');
            fetchRutas();
        } catch (error) {
            console.error('Error seeding templates:', error);
            toast.error(error.response?.data?.detail || 'Error al crear plantillas');
        } finally {
            setSeeding(false);
        }
    };

    const handleOpenRutaDialog = (ruta = null) => {
        if (ruta) {
            setRutaForm({
                nombre: ruta.nombre || '',
                descripcion: ruta.descripcion || '',
                codigo: ruta.codigo || '',
                es_default: ruta.es_default || false,
                requiere_edp: ruta.requiere_edp || false,
                requiere_orden_compra: ruta.requiere_orden_compra || false,
                dia_envio_edp: ruta.dia_envio_edp || '',
                dia_limite_oc: ruta.dia_limite_oc || '',
                dia_facturacion: ruta.dia_facturacion || '',
                dias_pago_default: ruta.dias_pago_default || 30,
                dias_gracia_mora: ruta.dias_gracia_mora || 5
            });
        } else {
            setRutaForm({
                nombre: '',
                descripcion: '',
                codigo: '',
                es_default: false,
                requiere_edp: false,
                requiere_orden_compra: false,
                dia_envio_edp: '',
                dia_limite_oc: '',
                dia_facturacion: '',
                dias_pago_default: 30,
                dias_gracia_mora: 5
            });
        }
        setDialogOpen(true);
    };

    const handleSaveRuta = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...rutaForm,
                dia_envio_edp: rutaForm.dia_envio_edp ? parseInt(rutaForm.dia_envio_edp) : null,
                dia_limite_oc: rutaForm.dia_limite_oc ? parseInt(rutaForm.dia_limite_oc) : null,
                dia_facturacion: rutaForm.dia_facturacion ? parseInt(rutaForm.dia_facturacion) : null,
            };

            if (selectedRuta) {
                await axios.put(`${API_URL}/api/rutas-cobro/${selectedRuta.id}`, data);
                toast.success('Ruta actualizada');
            } else {
                await axios.post(`${API_URL}/api/rutas-cobro`, data);
                toast.success('Ruta creada');
            }
            setDialogOpen(false);
            fetchRutas();
            if (selectedRuta) {
                fetchRutaDetail(selectedRuta.id);
            }
        } catch (error) {
            console.error('Error saving ruta:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar ruta');
        }
    };

    const handleDeleteRuta = async (rutaId) => {
        if (!window.confirm('¿Está seguro de eliminar esta ruta?')) return;
        try {
            await axios.delete(`${API_URL}/api/rutas-cobro/${rutaId}`);
            toast.success('Ruta eliminada');
            setSelectedRuta(null);
            fetchRutas();
        } catch (error) {
            console.error('Error deleting ruta:', error);
            toast.error(error.response?.data?.detail || 'Error al eliminar ruta');
        }
    };

    const handleOpenEtapaDialog = (etapa = null) => {
        if (etapa) {
            setEditingEtapa(etapa);
            setEtapaForm({
                nombre: etapa.nombre || '',
                codigo: etapa.codigo || '',
                orden: etapa.orden || 0,
                tipo_trigger: etapa.tipo_trigger || 'manual',
                dias_trigger: etapa.dias_trigger || '',
                dia_mes_trigger: etapa.dia_mes_trigger || '',
                documento_requerido: etapa.documento_requerido || 'ninguno',
                bloquea_avance_sin_documento: etapa.bloquea_avance_sin_documento || false,
                accion_entrada: etapa.accion_entrada || 'ninguna',
                genera_alerta: etapa.genera_alerta || false,
                mensaje_alerta: etapa.mensaje_alerta || '',
                color: etapa.color || '#3B82F6',
                es_etapa_final: etapa.es_etapa_final || false,
                es_etapa_error: etapa.es_etapa_error || false
            });
        } else {
            setEditingEtapa(null);
            const nextOrden = selectedRuta?.etapas?.length || 0;
            setEtapaForm({
                nombre: '',
                codigo: '',
                orden: nextOrden,
                tipo_trigger: 'manual',
                dias_trigger: '',
                dia_mes_trigger: '',
                documento_requerido: 'ninguno',
                bloquea_avance_sin_documento: false,
                accion_entrada: 'ninguna',
                genera_alerta: false,
                mensaje_alerta: '',
                color: '#3B82F6',
                es_etapa_final: false,
                es_etapa_error: false
            });
        }
        setEtapaDialogOpen(true);
    };

    const handleSaveEtapa = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...etapaForm,
                dias_trigger: etapaForm.dias_trigger ? parseInt(etapaForm.dias_trigger) : null,
                dia_mes_trigger: etapaForm.dia_mes_trigger ? parseInt(etapaForm.dia_mes_trigger) : null,
            };

            if (editingEtapa) {
                await axios.put(`${API_URL}/api/rutas-cobro/${selectedRuta.id}/etapas/${editingEtapa.id}`, data);
                toast.success('Etapa actualizada');
            } else {
                await axios.post(`${API_URL}/api/rutas-cobro/${selectedRuta.id}/etapas`, data);
                toast.success('Etapa creada');
            }
            setEtapaDialogOpen(false);
            fetchRutaDetail(selectedRuta.id);
            fetchRutas();
        } catch (error) {
            console.error('Error saving etapa:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar etapa');
        }
    };

    const handleDeleteEtapa = async (etapaId) => {
        if (!window.confirm('¿Está seguro de eliminar esta etapa?')) return;
        try {
            await axios.delete(`${API_URL}/api/rutas-cobro/${selectedRuta.id}/etapas/${etapaId}`);
            toast.success('Etapa eliminada');
            fetchRutaDetail(selectedRuta.id);
            fetchRutas();
        } catch (error) {
            console.error('Error deleting etapa:', error);
            toast.error(error.response?.data?.detail || 'Error al eliminar etapa');
        }
    };

    const getTriggerLabel = (tipo) => {
        const labels = {
            'manual': 'Manual',
            'fecha_fija_mes': 'Día fijo del mes',
            'dias_desde_evento': 'Días desde evento anterior',
            'dias_desde_factura': 'Días desde factura',
            'dias_desde_inicio': 'Días desde inicio',
            'documento_recibido': 'Al recibir documento',
            'pago_recibido': 'Al recibir pago',
            'automatico': 'Automático'
        };
        return labels[tipo] || tipo;
    };

    const getDocumentoLabel = (tipo) => {
        const labels = {
            'ninguno': 'Ninguno',
            'edp': 'Estado de Pago (EDP)',
            'orden_compra': 'Orden de Compra',
            'factura': 'Factura',
            'comprobante_pago': 'Comprobante de Pago',
            'aprobacion_cliente': 'Aprobación del Cliente'
        };
        return labels[tipo] || tipo;
    };

    const getAccionLabel = (tipo) => {
        const labels = {
            'ninguna': 'Ninguna',
            'generar_alerta': 'Generar alerta',
            'enviar_notificacion': 'Enviar notificación',
            'bloquear_facturacion': 'Bloquear facturación',
            'habilitar_facturacion': 'Habilitar facturación',
            'calcular_vencimiento': 'Calcular vencimiento',
            'marcar_mora': 'Marcar en mora',
            'generar_tarea': 'Generar tarea'
        };
        return labels[tipo] || tipo;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-[#5B9A9A]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Rutas de Cobro</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Configure los flujos de trabajo para el proceso de cobranza
                    </p>
                </div>
                <div className="flex gap-2">
                    {rutas.length === 0 && (
                        <Button 
                            variant="outline" 
                            onClick={handleSeedTemplates}
                            disabled={seeding}
                            data-testid="seed-templates-btn"
                        >
                            {seeding ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Copy className="mr-2 h-4 w-4" />
                            )}
                            Crear Plantillas
                        </Button>
                    )}
                    {canEdit() && (
                        <Button onClick={() => { setSelectedRuta(null); handleOpenRutaDialog(); }} data-testid="new-ruta-btn">
                            <Plus size={18} className="mr-2" />
                            Nueva Ruta
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Lista de Rutas */}
                <div className="lg:col-span-1 space-y-4">
                    <h3 className="font-semibold text-slate-700">Rutas Disponibles</h3>
                    {rutas.length === 0 ? (
                        <Card className="p-8 text-center">
                            <Route className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                            <p className="text-slate-500 mb-4">No hay rutas configuradas</p>
                            <p className="text-sm text-slate-400">
                                Haga clic en "Crear Plantillas" para comenzar con rutas predefinidas
                            </p>
                        </Card>
                    ) : (
                        rutas.map((ruta) => (
                            <Card 
                                key={ruta.id}
                                className={`cursor-pointer transition-all hover:shadow-md ${
                                    selectedRuta?.id === ruta.id ? 'ring-2 ring-blue-500' : ''
                                }`}
                                onClick={() => fetchRutaDetail(ruta.id)}
                                data-testid={`ruta-card-${ruta.codigo}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-semibold text-slate-900">{ruta.nombre}</h4>
                                                {ruta.es_default && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        <Star size={12} className="mr-1" />
                                                        Default
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-slate-500 mt-1">{ruta.codigo}</p>
                                            <div className="flex gap-2 mt-2">
                                                {ruta.requiere_edp && (
                                                    <Badge variant="outline" className="text-xs">EDP</Badge>
                                                )}
                                                {ruta.requiere_orden_compra && (
                                                    <Badge variant="outline" className="text-xs">OC</Badge>
                                                )}
                                                <Badge variant="outline" className="text-xs">
                                                    {ruta.total_etapas} etapas
                                                </Badge>
                                            </div>
                                        </div>
                                        <ChevronRight className="text-slate-400" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>

                {/* Detalle de Ruta */}
                <div className="lg:col-span-2">
                    {selectedRuta ? (
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        {selectedRuta.nombre}
                                        {selectedRuta.es_default && (
                                            <Badge className="bg-yellow-100 text-yellow-800">
                                                <Star size={12} className="mr-1" />
                                                Default
                                            </Badge>
                                        )}
                                    </CardTitle>
                                    <p className="text-sm text-slate-500 mt-1">{selectedRuta.descripcion}</p>
                                </div>
                                {canEdit() && (
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => handleOpenRutaDialog(selectedRuta)}
                                        >
                                            <Edit size={14} className="mr-1" />
                                            Editar
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => handleDeleteRuta(selectedRuta.id)}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {/* Configuración de la ruta */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg mb-6">
                                    {selectedRuta.dia_envio_edp && (
                                        <div>
                                            <p className="text-xs text-slate-500">Día envío EDP</p>
                                            <p className="font-semibold">{selectedRuta.dia_envio_edp}</p>
                                        </div>
                                    )}
                                    {selectedRuta.dia_limite_oc && (
                                        <div>
                                            <p className="text-xs text-slate-500">Día límite OC</p>
                                            <p className="font-semibold">{selectedRuta.dia_limite_oc}</p>
                                        </div>
                                    )}
                                    {selectedRuta.dia_facturacion && (
                                        <div>
                                            <p className="text-xs text-slate-500">Día facturación</p>
                                            <p className="font-semibold">{selectedRuta.dia_facturacion}</p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-xs text-slate-500">Días de pago</p>
                                        <p className="font-semibold">{selectedRuta.dias_pago_default}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">Gracia mora</p>
                                        <p className="font-semibold">{selectedRuta.dias_gracia_mora} días</p>
                                    </div>
                                </div>

                                {/* Etapas */}
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="font-semibold text-slate-700">Etapas del Proceso</h4>
                                    {canEdit() && (
                                        <Button size="sm" onClick={() => handleOpenEtapaDialog()}>
                                            <Plus size={14} className="mr-1" />
                                            Agregar Etapa
                                        </Button>
                                    )}
                                </div>

                                {/* Timeline de etapas */}
                                <div className="space-y-2">
                                    {selectedRuta.etapas?.map((etapa, index) => (
                                        <div 
                                            key={etapa.id}
                                            className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:bg-slate-50"
                                        >
                                            <div 
                                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                                style={{ backgroundColor: etapa.color }}
                                            >
                                                {index + 1}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{etapa.nombre}</span>
                                                    {etapa.es_etapa_final && (
                                                        <Badge className="bg-green-100 text-green-800 text-xs">Final</Badge>
                                                    )}
                                                    {etapa.es_etapa_error && (
                                                        <Badge className="bg-red-100 text-red-800 text-xs">Error</Badge>
                                                    )}
                                                    {etapa.genera_alerta && (
                                                        <Badge variant="outline" className="text-xs">Alerta</Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                                                    <span>Trigger: {getTriggerLabel(etapa.tipo_trigger)}</span>
                                                    {etapa.dias_trigger && (
                                                        <span>{etapa.dias_trigger} días</span>
                                                    )}
                                                    {etapa.dia_mes_trigger && (
                                                        <span>Día {etapa.dia_mes_trigger}</span>
                                                    )}
                                                    {etapa.documento_requerido !== 'ninguno' && (
                                                        <span>Req: {getDocumentoLabel(etapa.documento_requerido)}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {canEdit() && (
                                                <div className="flex gap-1">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={(e) => { e.stopPropagation(); handleOpenEtapaDialog(etapa); }}
                                                    >
                                                        <Edit size={14} />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        className="text-red-600"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteEtapa(etapa.id); }}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="h-full flex items-center justify-center p-12">
                            <div className="text-center">
                                <Settings2 className="mx-auto h-16 w-16 text-slate-200 mb-4" />
                                <h3 className="text-lg font-semibold text-slate-600">Seleccione una ruta</h3>
                                <p className="text-sm text-slate-400 mt-2">
                                    Haga clic en una ruta de la lista para ver y editar sus etapas
                                </p>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            {/* Dialog para crear/editar ruta */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedRuta ? 'Editar Ruta de Cobro' : 'Nueva Ruta de Cobro'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveRuta}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="nombre">Nombre *</Label>
                                <Input
                                    id="nombre"
                                    value={rutaForm.nombre}
                                    onChange={(e) => setRutaForm({...rutaForm, nombre: e.target.value})}
                                    required
                                    placeholder="Ej: Pago Directo"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="codigo">Código</Label>
                                <Input
                                    id="codigo"
                                    value={rutaForm.codigo}
                                    onChange={(e) => setRutaForm({...rutaForm, codigo: e.target.value.toUpperCase()})}
                                    placeholder="Ej: PAGO_DIRECTO"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="dias_pago_default">Días de pago default</Label>
                                <Input
                                    id="dias_pago_default"
                                    type="number"
                                    value={rutaForm.dias_pago_default}
                                    onChange={(e) => setRutaForm({...rutaForm, dias_pago_default: parseInt(e.target.value)})}
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="descripcion">Descripción</Label>
                                <Textarea
                                    id="descripcion"
                                    value={rutaForm.descripcion}
                                    onChange={(e) => setRutaForm({...rutaForm, descripcion: e.target.value})}
                                    rows={2}
                                />
                            </div>
                            
                            <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg space-y-4">
                                <h4 className="font-medium text-slate-700">Requisitos del proceso</h4>
                                <div className="flex flex-wrap gap-6">
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={rutaForm.es_default}
                                            onCheckedChange={(checked) => setRutaForm({...rutaForm, es_default: checked})}
                                        />
                                        <Label>Ruta por defecto</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={rutaForm.requiere_edp}
                                            onCheckedChange={(checked) => setRutaForm({...rutaForm, requiere_edp: checked})}
                                        />
                                        <Label>Requiere EDP</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={rutaForm.requiere_orden_compra}
                                            onCheckedChange={(checked) => setRutaForm({...rutaForm, requiere_orden_compra: checked})}
                                        />
                                        <Label>Requiere OC</Label>
                                    </div>
                                </div>
                            </div>

                            {(rutaForm.requiere_edp || rutaForm.requiere_orden_compra) && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="dia_envio_edp">Día envío EDP</Label>
                                        <Input
                                            id="dia_envio_edp"
                                            type="number"
                                            min="1"
                                            max="31"
                                            value={rutaForm.dia_envio_edp}
                                            onChange={(e) => setRutaForm({...rutaForm, dia_envio_edp: e.target.value})}
                                            placeholder="25"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="dia_limite_oc">Día límite OC</Label>
                                        <Input
                                            id="dia_limite_oc"
                                            type="number"
                                            min="1"
                                            max="31"
                                            value={rutaForm.dia_limite_oc}
                                            onChange={(e) => setRutaForm({...rutaForm, dia_limite_oc: e.target.value})}
                                            placeholder="2"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="dia_facturacion">Día facturación</Label>
                                        <Input
                                            id="dia_facturacion"
                                            type="number"
                                            min="1"
                                            max="31"
                                            value={rutaForm.dia_facturacion}
                                            onChange={(e) => setRutaForm({...rutaForm, dia_facturacion: e.target.value})}
                                            placeholder="3"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="dias_gracia_mora">Días gracia mora</Label>
                                        <Input
                                            id="dias_gracia_mora"
                                            type="number"
                                            value={rutaForm.dias_gracia_mora}
                                            onChange={(e) => setRutaForm({...rutaForm, dias_gracia_mora: parseInt(e.target.value)})}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit">
                                {selectedRuta ? 'Guardar cambios' : 'Crear ruta'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog para crear/editar etapa */}
            <Dialog open={etapaDialogOpen} onOpenChange={setEtapaDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingEtapa ? 'Editar Etapa' : 'Nueva Etapa'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveEtapa}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 max-h-[60vh] overflow-y-auto">
                            <div className="space-y-2">
                                <Label htmlFor="etapa_nombre">Nombre *</Label>
                                <Input
                                    id="etapa_nombre"
                                    value={etapaForm.nombre}
                                    onChange={(e) => setEtapaForm({...etapaForm, nombre: e.target.value})}
                                    required
                                    placeholder="Ej: Servicio en Curso"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="etapa_codigo">Código *</Label>
                                <Input
                                    id="etapa_codigo"
                                    value={etapaForm.codigo}
                                    onChange={(e) => setEtapaForm({...etapaForm, codigo: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                                    required
                                    placeholder="Ej: en_curso"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="etapa_orden">Orden</Label>
                                <Input
                                    id="etapa_orden"
                                    type="number"
                                    value={etapaForm.orden}
                                    onChange={(e) => setEtapaForm({...etapaForm, orden: parseInt(e.target.value)})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="etapa_color">Color</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="etapa_color"
                                        type="color"
                                        value={etapaForm.color}
                                        onChange={(e) => setEtapaForm({...etapaForm, color: e.target.value})}
                                        className="w-16 p-1 h-10"
                                    />
                                    <Input
                                        value={etapaForm.color}
                                        onChange={(e) => setEtapaForm({...etapaForm, color: e.target.value})}
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tipo_trigger">Tipo de Trigger</Label>
                                <Select 
                                    value={etapaForm.tipo_trigger}
                                    onValueChange={(v) => setEtapaForm({...etapaForm, tipo_trigger: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="manual">Manual</SelectItem>
                                        <SelectItem value="automatico">Automático</SelectItem>
                                        <SelectItem value="fecha_fija_mes">Día fijo del mes</SelectItem>
                                        <SelectItem value="dias_desde_evento">Días desde evento anterior</SelectItem>
                                        <SelectItem value="dias_desde_factura">Días desde factura</SelectItem>
                                        <SelectItem value="documento_recibido">Al recibir documento</SelectItem>
                                        <SelectItem value="pago_recibido">Al recibir pago</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {(etapaForm.tipo_trigger === 'dias_desde_evento' || 
                              etapaForm.tipo_trigger === 'dias_desde_factura' ||
                              etapaForm.tipo_trigger === 'dias_desde_inicio') && (
                                <div className="space-y-2">
                                    <Label htmlFor="dias_trigger">Días</Label>
                                    <Input
                                        id="dias_trigger"
                                        type="number"
                                        value={etapaForm.dias_trigger}
                                        onChange={(e) => setEtapaForm({...etapaForm, dias_trigger: e.target.value})}
                                        placeholder="30"
                                    />
                                </div>
                            )}
                            {etapaForm.tipo_trigger === 'fecha_fija_mes' && (
                                <div className="space-y-2">
                                    <Label htmlFor="dia_mes_trigger">Día del mes</Label>
                                    <Input
                                        id="dia_mes_trigger"
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={etapaForm.dia_mes_trigger}
                                        onChange={(e) => setEtapaForm({...etapaForm, dia_mes_trigger: e.target.value})}
                                        placeholder="25"
                                    />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="documento_requerido">Documento Requerido</Label>
                                <Select 
                                    value={etapaForm.documento_requerido}
                                    onValueChange={(v) => setEtapaForm({...etapaForm, documento_requerido: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ninguno">Ninguno</SelectItem>
                                        <SelectItem value="edp">Estado de Pago (EDP)</SelectItem>
                                        <SelectItem value="orden_compra">Orden de Compra</SelectItem>
                                        <SelectItem value="factura">Factura</SelectItem>
                                        <SelectItem value="aprobacion_cliente">Aprobación Cliente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="accion_entrada">Acción al entrar</Label>
                                <Select 
                                    value={etapaForm.accion_entrada}
                                    onValueChange={(v) => setEtapaForm({...etapaForm, accion_entrada: v})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ninguna">Ninguna</SelectItem>
                                        <SelectItem value="generar_alerta">Generar alerta</SelectItem>
                                        <SelectItem value="calcular_vencimiento">Calcular vencimiento</SelectItem>
                                        <SelectItem value="habilitar_facturacion">Habilitar facturación</SelectItem>
                                        <SelectItem value="bloquear_facturacion">Bloquear facturación</SelectItem>
                                        <SelectItem value="marcar_mora">Marcar en mora</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg space-y-4">
                                <h4 className="font-medium text-slate-700">Opciones adicionales</h4>
                                <div className="flex flex-wrap gap-6">
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={etapaForm.bloquea_avance_sin_documento}
                                            onCheckedChange={(checked) => setEtapaForm({...etapaForm, bloquea_avance_sin_documento: checked})}
                                        />
                                        <Label className="text-sm">Bloquear avance sin documento</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={etapaForm.genera_alerta}
                                            onCheckedChange={(checked) => setEtapaForm({...etapaForm, genera_alerta: checked})}
                                        />
                                        <Label className="text-sm">Genera alerta</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={etapaForm.es_etapa_final}
                                            onCheckedChange={(checked) => setEtapaForm({...etapaForm, es_etapa_final: checked})}
                                        />
                                        <Label className="text-sm">Etapa final</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={etapaForm.es_etapa_error}
                                            onCheckedChange={(checked) => setEtapaForm({...etapaForm, es_etapa_error: checked})}
                                        />
                                        <Label className="text-sm">Etapa de error/mora</Label>
                                    </div>
                                </div>
                            </div>

                            {etapaForm.genera_alerta && (
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="mensaje_alerta">Mensaje de alerta</Label>
                                    <Input
                                        id="mensaje_alerta"
                                        value={etapaForm.mensaje_alerta}
                                        onChange={(e) => setEtapaForm({...etapaForm, mensaje_alerta: e.target.value})}
                                        placeholder="Ej: Factura pendiente de pago"
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEtapaDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit">
                                {editingEtapa ? 'Guardar cambios' : 'Crear etapa'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RutasCobro;
